 
import React from 'react';
import { UncontrolledTooltip } from 'reactstrap';
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    ColumnDef,
    flexRender,
} from '@tanstack/react-table';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import {
    NSTable,
    NSCellInput,
    NSBadge,
} from 'bricks';
import RenderIf from 'components/RenderIf/RenderIf';
import IconWithTooltip from 'components/IconWithTooltip/IconWithTooltip';
import {
    OPERATIONS_INCOME_WS_HANDLERS,
    CASH_FLOW_TOOLTIP_MESSAGE,
    tableValueClassName,
    RSF_BUILT_GSF_DISCREPANCY_TOOLTIP_OPERATIONS_TEXT,
} from 'views/ProFormaTable/constants';
import { doesRentableSFExceedBuiltGSF } from 'views/ProFormaTable/helpers';
import { ProFormaModelIds } from 'constants/proForma';
import { IIncomeFloorEstimate } from 'views/ProFormaTable/types';
import { useProFormaSocketContext } from '../../../../socketContext/ProFormaSocketProvider';
import { useFloorAllocations } from '../../hooks/useFloorAllocations';
import './FloorAllocationsTable.scss';

export interface IFloorAllocationsTableProps {
    operationBeginIndex: number;
    baseCashFlowColumns: ColumnDef<any>[];
}

const FloorAllocationsTable = ({
    operationBeginIndex, baseCashFlowColumns,
}: IFloorAllocationsTableProps) => {
    const {
        incomeSummary, handleCreateOperationsRow, handleUpdateOperationsRow, handleDeleteOperationsRow,
        showCents, formatDisplayValue, operationsCashFlow, proForma,
    } = useProFormaSocketContext();

    const {
        incompleteAllocationFloors, isLastRowOfFloor,
    } = useFloorAllocations(incomeSummary?.incomeFloorEstimates, proForma?.averageStories);

    const isApartmentWithRetail = proForma.proFormaModelId === ProFormaModelIds.APARTMENT_WITH_RETAIL;
    const rentalIncomeTotals = isApartmentWithRetail ? incomeSummary.totalRetailIncome : incomeSummary.totalRentalIncome;
    const shouldShowRSFWarning = isApartmentWithRetail
        ? doesRentableSFExceedBuiltGSF(Number(proForma.retailBuiltGrossSquareFootage), incomeSummary.totalRetailIncome?.totalSquareFootage)
        : doesRentableSFExceedBuiltGSF(
            Number(proForma.builtGrossSquareFootage),
            incomeSummary.totalRentableSf,
        );

    const handleUpdate = (id: number, field: string) => handleUpdateOperationsRow(OPERATIONS_INCOME_WS_HANDLERS.ESTIMATE.UPDATE, id, field);

    const handleAddRow = () => {
        handleCreateOperationsRow(OPERATIONS_INCOME_WS_HANDLERS.ESTIMATE.CREATE);
    };

    const shouldShowIncompleteFloorWarning = (
        id: number, floor: number,
    ): boolean => incompleteAllocationFloors.has(floor) && isLastRowOfFloor(id, floor);

    const cashFlowColumns: ColumnDef<IIncomeFloorEstimate>[] = baseCashFlowColumns.map((column, cashFlowIndex) => ({
        ...column,
        id: `floor-allocation-period-${column.id}`,
        accessorFn: (row, index: number) => (
            typeof operationsCashFlow?.[operationBeginIndex]?.incomeFloorEstimates?.[index]?.value === 'number'
                ? (operationsCashFlow?.[cashFlowIndex]?.incomeFloorEstimates?.[index]?.value || 0)
                : null
        ),
        footer: () => (
            <span className="text-dark">
                {operationBeginIndex
                    ? formatDisplayValue(
                        operationsCashFlow?.[cashFlowIndex]?.[isApartmentWithRetail ? 'totalRetailIncome' : 'totalRentalIncome'] || 0,
                        showCents, 'currency',
                    )
                    : ' '}
            </span>
        ),
    }));

    const floorAllocationsColumns: ColumnDef<IIncomeFloorEstimate>[] = [
        {
            accessorKey: 'floor',
            id: 'floor',
            header: () => <span className="pl-1">Floor</span>,
            cell: ({ row, getValue }) => (
                <span className={`ml-2 ${tableValueClassName(getValue() || 0)}`}>
                    {formatDisplayValue(getValue() || 0, false)}
                </span>
            ),
            footer: () => <span className="pl-1 text-nowrap">Total rental income</span>,
        },
        {
            accessorKey: 'allocationPercentage',
            id: 'allocationPercentage',
            header: () => (
                <div className="d-flex align-items-center justify-content-between w-100 pr-1">
                    <span>Allocation</span>
                    <div id="floor-allocation-table-plus-icon">
                        <FontAwesomeIcon
                            id="plus-icon"
                            icon={faPlus}
                            className="ml-2 text-muted cursor--pointer"
                            onClick={() => handleAddRow()}
                        />
                    </div>
                </div>
            ),
            cell: ({ row, getValue }) => (
                <div className="d-flex align-items-center justify-content-between w-100 pr-1">
                    <div className="d-flex align-items-center w-100 ml-n1">
                        <NSCellInput
                            type="number"
                            numberType="percentage"
                            showCents
                            value={getValue()}
                            handleUpdateValue={updatedValue => {
                                const updateFn = handleUpdate(Number(row.original.id), 'allocationPercentage');
                                if (typeof updateFn === 'function') {
                                    updateFn({
                                        target: { value: updatedValue },
                                    } as React.ChangeEvent<HTMLInputElement>);
                                }
                            }}
                            placeholder="Enter percentage"
                        />
                        <RenderIf isTrue={shouldShowIncompleteFloorWarning(Number(row.original.id), Number(row.original.floor))}>
                            <div
                                id={`incomplete-floor-warning-${row.original.id}`}
                                className="pl-2"
                            >
                                <NSBadge color="warning-lighten">
                                    <FontAwesomeIcon
                                        icon={faTriangleExclamation}
                                    />
                                    <span className="pl-1">
                                        {'Allocation < 100%'}
                                    </span>
                                </NSBadge>
                                <UncontrolledTooltip target={`incomplete-floor-warning-${row.original.id}`}>
                                    Total allocation for a floor must equal 100%.
                                </UncontrolledTooltip>
                            </div>
                        </RenderIf>
                    </div>
                    <FontAwesomeIcon
                        id="trash-icon"
                        icon={faTrash}
                        className="ml-1 text-dark cursor--pointer"
                        onClick={() => handleDeleteOperationsRow(OPERATIONS_INCOME_WS_HANDLERS.ESTIMATE.DELETE, Number(row.original.id))}
                    />
                </div>
            ),
        },
        {
            accessorKey: 'squareFootage',
            id: 'squareFootage',
            header: () => (
                <div className={classNames({ 'd-flex': shouldShowRSFWarning })}>
                    <RenderIf isTrue={shouldShowRSFWarning}>
                        <IconWithTooltip
                            name={`RSFDiscrepancyIcon-${proForma.proFormaModelId}`}
                            icon={faTriangleExclamation}
                            tooltipText={RSF_BUILT_GSF_DISCREPANCY_TOOLTIP_OPERATIONS_TEXT}
                            iconClassName="text-warning"
                            className="my-auto mx-1"
                        />
                    </RenderIf>
                    <span>RSF</span>
                </div>
            ),
            cell: ({ row, getValue }) => (
                <span className={tableValueClassName(getValue() || 0)}>
                    {formatDisplayValue(getValue() || 0, false)}
                </span>
            ),
            footer: () => <span>{formatDisplayValue(rentalIncomeTotals?.totalSquareFootage || 0, false)}</span>,
        },
        {
            accessorKey: 'valuePerSfPerYear',
            id: 'valuePerSfPerYear',
            header: () => <span>$/RSF/year</span>,
            cell: ({ row, getValue }) => (
                <NSCellInput
                    showCents
                    type="number"
                    numberType="currency"
                    value={getValue()}
                    handleUpdateValue={updatedValue => {
                        const updateFn = handleUpdate(Number(row.original.id), 'valuePerSfPerYear');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value: updatedValue },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    isRightAligned
                    placeholder="Enter amount"
                />
            ),
            footer: () => <span>{formatDisplayValue(rentalIncomeTotals?.totalValuePerSfPerYear || 0, true, 'currency')}</span>,
        },
        {
            accessorKey: 'annualRent',
            id: 'annualRent',
            header: () => (
                <div className="pr-1">
                    <span>Annual rent</span>
                    <span className="text-nowrap"> (yr 1)</span>
                </div>
            ),
            cell: ({ row, getValue }) => (
                <div className="pr-1">
                    <NSCellInput
                        showCents={showCents}
                        type="number"
                        numberType="currency"
                        value={getValue()}
                        handleUpdateValue={updatedValue => {
                            const updateFn = handleUpdate(Number(row.original.id), 'annualRent');
                            if (typeof updateFn === 'function') {
                                updateFn({
                                    target: { value: updatedValue },
                                } as React.ChangeEvent<HTMLInputElement>);
                            }
                        }}
                        isRightAligned
                        placeholder="Enter amount"
                    />
                </div>
            ),
            footer: () => (
                <span className="pr-1">
                    {formatDisplayValue(rentalIncomeTotals?.totalAnnualRent || 0, showCents, 'currency')}
                </span>
            ),
        },
        ...cashFlowColumns,
    ];

    const table = useReactTable({
        data: (incomeSummary.incomeFloorEstimates || []).sort((a, b) => a.floor - b.floor),
        columns: floorAllocationsColumns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    return (
        <NSTable className="NSTable FloorAllocationsTable mb-1">
            <thead className="NSTable__thead">
                {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id} className="NSTable__thead__tr">
                        {headerGroup.headers.map(header => (
                            <th key={header.id} className="NSTable__thead__tr__th p-1">
                                {header.isPlaceholder ? null : (
                                    <div>
                                        {flexRender(
                                            header.column.columnDef.header,
                                            header.getContext(),
                                        )}
                                    </div>
                                )}
                            </th>
                        ))}
                    </tr>
                ))}
            </thead>
            <tbody
                className={classNames('NSTable__tbody', {
                    'NSTable__tbody--pro-forma-placeholder': !table.getRowModel().rows.length,
                })}
            >
                <RenderIf isTrue={table.getRowModel().rows.length}>
                    {table.getRowModel().rows.map(row => (
                        <tr key={row.id} className="NSTable__tbody__tr NSTable__tbody__tr--level-1">
                            {row.getVisibleCells().map((cell, cellIndex) => (
                                <td
                                    key={cell.id}
                                    className={classNames(['NSTable__tbody__tr__td p-1', {
                                        'NSTable__tbody__tr__td--warning-border': cellIndex === 0
                                            && shouldShowIncompleteFloorWarning(
                                                Number(row.original.id), Number(row.original.floor),
                                            ),
                                    }])}
                                >
                                    {flexRender(
                                        cell.column.columnDef.cell,
                                        cell.getContext(),
                                    )}
                                </td>
                            ))}
                        </tr>
                    ))}
                </RenderIf>
                <RenderIf isTrue={!table.getRowModel().rows.length}>
                    <tr className="NSTable__tbody__tr NSTable__tbody__tr--level-1">
                        <td colSpan={5} className="NSTable__tbody__tr__td text-center text-muted py-4">
                            <div className="text-dark">Your added floor allocations will appear here.</div>
                            <div className="small">
                                Click the
                                {' '}
                                <span className="font-weight-bold text-dark">[+]</span>
                                {' '}
                                symbol in the
                                {' '}
                                <span className="font-weight-bold text-dark">Allocation</span>
                                {' '}
                                header to add your first floor allocation.
                            </div>
                        </td>
                        {baseCashFlowColumns.map((column, index) => (
                            <td
                                key={`floor-allocation-period-${column.id}`}
                                className="NSTable__tbody__tr__td"
                                id={`floor-allocation-placeholder-period-${index}`}
                            >
                                <RenderIf isTrue={baseCashFlowColumns.length === 1 && index === 0}>
                                    <UncontrolledTooltip target={`floor-allocation-placeholder-period-${index}`}>
                                        {CASH_FLOW_TOOLTIP_MESSAGE}
                                    </UncontrolledTooltip>
                                </RenderIf>
                            </td>
                        ))}
                    </tr>
                </RenderIf>
            </tbody>
            <tfoot className="NSTable__tfoot">
                {table.getFooterGroups().map(footerGroup => (
                    <tr key={footerGroup.id} className="NSTable__tfoot__tr">
                        {footerGroup.headers.map(header => (
                            <th key={header.id} className="NSTable__tfoot__tr__th p-1">
                                {flexRender(
                                    header.column.columnDef.footer,
                                    header.getContext(),
                                )}
                            </th>
                        ))}
                    </tr>
                ))}
            </tfoot>
        </NSTable>
    );
};

export default FloorAllocationsTable;
